import { useState } from 'react'
import {
  Button,
  Checkbox,
  Divider,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material'
import ViewColumnIcon from '@mui/icons-material/ViewColumn'

export interface ColumnDef {
  id: string
  label: string
  /** Columns flagged `alwaysVisible: true` cannot be hidden (e.g. row id, actions). */
  alwaysVisible?: boolean
}

export interface ColumnVisibilityMenuProps {
  columns: ColumnDef[]
  hidden: string[]
  onToggle: (id: string) => void
  onReset?: () => void
  buttonLabel?: string
}

/**
 * Column-visibility dropdown for tables. Pair with `usePersistedTableState`
 * so the user's choice survives reloads.
 */
export default function ColumnVisibilityMenu({
  columns,
  hidden,
  onToggle,
  onReset,
  buttonLabel = 'Columns',
}: ColumnVisibilityMenuProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const open = Boolean(anchorEl)

  const visibleCount = columns.length - hidden.length

  return (
    <>
      <Tooltip title="Show / hide columns">
        <Button
          size="small"
          variant="outlined"
          startIcon={<ViewColumnIcon />}
          onClick={(e) => setAnchorEl(e.currentTarget)}
        >
          {buttonLabel} ({visibleCount}/{columns.length})
        </Button>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        keepMounted
      >
        {columns.map((c) => {
          const isHidden = hidden.includes(c.id)
          return (
            <MenuItem
              key={c.id}
              dense
              disabled={c.alwaysVisible}
              onClick={() => {
                if (!c.alwaysVisible) onToggle(c.id)
              }}
            >
              <Checkbox
                size="small"
                checked={!isHidden}
                disabled={c.alwaysVisible}
                sx={{ p: 0.5, mr: 1 }}
              />
              <ListItemText
                primary={c.label}
                secondary={c.alwaysVisible ? 'always shown' : undefined}
              />
            </MenuItem>
          )
        })}
        {onReset && (
          <>
            <Divider />
            <MenuItem
              dense
              onClick={() => {
                onReset()
                setAnchorEl(null)
              }}
            >
              <ListItemText primary="Reset to defaults" />
            </MenuItem>
          </>
        )}
      </Menu>
    </>
  )
}
